import React, { useContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";

import {
    Box,
    List,
    Tab,
    Tabs,
    Typography,
    useTheme,
    useMediaQuery
} from '@mui/material';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import LaptopIcon from '@mui/icons-material/Laptop';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import UndoIcon from '@mui/icons-material/Undo';

import PermissionGuard from '../contexts/PermissionContext';
import ProjectContext from '../contexts/ProjectContext';

import ItemList from '../components/menuitem/ItemList';

import ControlBar from '../components/structure/ControlBar';
import ControlButton from '../components/structure/ControlButton';


import CreateSourcePopover from '../components/popover/CreateSourcePopover';
import MenuPopover from '../components/popover/MenuPopover';
import MenuListItem from '../components/menuitem/MenuListItem';

import { itemIcons } from '../components/SideItems/SideItems';
import ControlDivider from '../components/structure/ControlDivider';
import SourceConfig from '../components/settings/SourceConfig';
import SourcesRightClick from '../components/rightclicks/SourcesRightClick';
import {useFetch} from '../components/auth/useFetch';


export default function SourceConfigView(props) {
    
    const viewname = 'Source Configuration'

    const { isAuthenticated } = useAuth0();
    const projectContext = useContext(ProjectContext)

    const [anchorEl, setAnchorEl] = useState(null)
    const [menuContent, setMenuContent] = useState(<></>)
    
    const [currentSource, setCurrentSource] = useState('')
    const [sources, setSources] = useState([])
    const [sourceProcessingConfig, setSourceProcessingConfig] = useState({})
    const [renderContext, setRenderContext] = useState({})

    const [savedUpdate, setSavedUpdate] = useState(0)


    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('md'))
    const fetchWithAuth = useFetch()

    useEffect(() => {
        if (isAuthenticated){
            getAllSources()
            getSourceRenderContext()
        }
    }, [isAuthenticated])


    const openMenu = (e) => {
        setAnchorEl(e.currentTarget)
    }

    const closeMenu = (e) => {
        setAnchorEl(null)
    }

    const openCreateSourceMenu = (e) => {

        openMenu(e)
        setMenuContent(
            <CreateSourcePopover
                closeMenu={closeMenu}
                setSources={setSources}
                setSourceProcessingConfig={setSourceProcessingConfig}
                sources={sources}
                setCurrentSource={setCurrentSource}
            />
        )
    }

    const openRevertMenu = (e) => {
        openMenu(e)
        setMenuContent(
            <div style={{ width: '200px'}}>
                <MenuListItem 
                    label={'Revert To Last Saved Config'}
                    icon={<UndoIcon style={{ color: 'grey' }} />}
                    onClick={() => undoConfig('revert')}
                />
                <MenuListItem
                    label={'Reset To Original Config'}
                    icon={<SettingsBackupRestoreIcon  style={{ color: 'grey' }} />}
                    onClick={() => undoConfig('reset')}
                />
            </div>
        )
        
    }

    const undoConfig = (undo) => {
        let url = `${process.env.REACT_APP_BACKEND_URL}/konsol/sources/?source_name=${currentSource}&function=${undo}`
        fetchWithAuth(url, {
            method: 'PATCH',
            headers:{
                'X-Project-ID': projectContext?.project_db
            },
        }).then(()=>{
            setSavedUpdate(savedUpdate + 1)
            closeMenu()
        })
        .catch(e => {
            console.error(e);
        })
    }

    async function deleteSource(clickedSource) {
        fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/sources/?source_name=${clickedSource}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'X-Project-ID': projectContext?.project_db
            },
        })
        .then(() => {
            setSources((prev) => prev.filter(source => source !== clickedSource))
            setSourceProcessingConfig({})
        })
        .catch(e => {
            console.error(e);
        })
    }

    async function getAllSources(){
        try{
            const deliverablesUrl = `${process.env.REACT_APP_BACKEND_URL}/konsol/sources`
            fetchWithAuth(deliverablesUrl, {
                headers: {
                    'X-Project-ID': projectContext?.project_db
                },
            })
                .then(res => res.json())
                .then(res => {
                    // Filter out sources that are of type 'integration-sync' - these are not ingested like regular sources
                    setSources(Object.keys(res).filter(key => res[key].type !== 'integration-sync').sort())
                })
        }
            catch (e) {
            console.error(e)
        }
    }

    const getSourceRenderContext = () => {
        let sourceConfigURL = `${process.env.REACT_APP_BACKEND_URL}/konsol/sources/meta`
        fetchWithAuth(sourceConfigURL, {
            headers:{
                'X-Project-ID': projectContext?.project_db
            },
        })
            .then(res => res.json())
            .then(res => setRenderContext(res))
            .catch(e => {
                console.error(e);
            })
    }

    const submitConfig = () => {
        let url = `${process.env.REACT_APP_BACKEND_URL}/konsol/sources/?source_name=${currentSource}`
        let body = validateSourceProcessingConfig()
        fetchWithAuth(url, {
            method: 'PUT',
            body: JSON.stringify(body),
            headers:{
                'Content-Type': 'application/json',
                'X-Project-ID': projectContext?.project_db
            },
        }).then(()=>{
            setSavedUpdate(savedUpdate + 1)
        })
        .catch(e => {
            console.error(e);
        })

    }


    const validateSourceProcessingConfig = () => {
        let data = structuredClone(sourceProcessingConfig)
        const processTransform = objectArrayToKeyValue((data?.process?.transform ?? [{}]), 'column', 'code')
        data = updateValue('process.transform', data, processTransform)
        const linkTransform = (data?.link ?? []).map(strategy => {
            return { ...strategy, transform: objectArrayToKeyValue((strategy?.transform ?? [{}]), 'column', 'code')}
        })
        data = updateValue('link', data, linkTransform)
        return data
    }

    function objectArrayToKeyValue(data, keyName, valueName){
        return data.reduce((acc, item) => {
            const key = item[keyName] !== undefined ? item[keyName] : '';
            const value = item[valueName] !== undefined ? item[valueName] : '';
            acc[key] = value;
            return acc;
        }, {});
    }

    function updateValue(path, obj, value) {
        let current = structuredClone(obj); // Create a deep copy of the previous state
        const keys = path.split('.');

        // Traverse the path except for the last key
        let temp = current;
        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];

            // Ensure the key exists and is of the correct type
            if (!(key in temp) || temp[key] === null) {
                const nextKey = keys[i + 1];

                // Create an array if the next key is a valid number, otherwise create an object
                temp[key] = isNaN(nextKey) || nextKey === '' ? {} : [];
            }

            // Move to the next level in the structure
            temp = temp[key];
        }

        const lastKey = keys[keys.length - 1];

        // If the value is undefined, delete the property
        if (value === undefined) {
            if (Array.isArray(temp) && !isNaN(lastKey)) {
                const index = parseInt(lastKey, 10);
                temp.splice(index, 1);
            } else {
                delete temp[lastKey];
            }
        } else {
            // If it's an array and the key is a valid number
            if (Array.isArray(temp) && !isNaN(lastKey)) {
                const index = parseInt(lastKey, 10);
                temp[index] = value;
            } else {
                temp[lastKey] = value; // Set value normally
            }
        }

        return current; // Return the updated state
    }


    return (
        <React.Fragment>
            {
                // If tablet or smaller, show page unavailable
                // else show page
                isSm
                ?
                <Box
                    width={'100%'}
                    height={'100%'}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                >
                    <LaptopIcon
                        sx={{
                            color: "rgba(128,128,128,0.4)",
                            fontSize: 100
                        }}
                    />
                    <Typography color={'rgba(128,128,128,1)'} component="span" variant="h7" align="left" gutterBottom>
                        Feature available on desktop/laptop
                    </Typography>
                </Box>
                :
                <Box display="flex" flexDirection="column" height='100%' overflow='hidden'>
                    <ControlBar>
                        <Tabs
                            value={0}
                            indicatorColor="secondary"
                            variant="scrollable"
                            scrollButtons="auto">
                            <Tab
                                label={
                                    <Box display="flex" alignItems="center">
                                        {itemIcons[viewname]}
                                        <Box ml={1}>{viewname}</Box>
                                    </Box>
                                }
                                style={{ textTransform: 'none', fontWeight: 'bold', color: 'white' }} />
                        </Tabs>
                        <PermissionGuard permission={['create:source']}>
                        <ControlDivider />
                        
                        <ControlButton
                            title='Create Source'
                            clicked={openCreateSourceMenu}
                        >
                            <CreateNewFolderIcon style={{fill: 'white'}} />
                        </ControlButton>
                        </PermissionGuard>

                        <ControlDivider />

                        <ControlButton
                            title='Save'
                            clicked={submitConfig}
                        >
                            <SaveIcon style={{fill: 'white'}} />
                        </ControlButton>
                        <PermissionGuard permission={['update:source']}>
                        <ControlButton
                            title='Revert to an older config'
                            clicked={openRevertMenu}
                            disabled={sourceProcessingConfig?.version ? false : true}
                        >
                            <Box
                                display="flex"
                                paddingLeft="5px"
                                justifyContent="center"
                                alignItems="center"
                                pr={0.3}
                            >
                                <ReplayIcon
                                    style={{
                                        fill: sourceProcessingConfig?.version ? 'white' : 'grey' 
                                    }}
                                />
                                <ArrowDropDownIcon
                                    style={{
                                        fill: sourceProcessingConfig?.version ? 'white' : 'grey' 
                                    }} 
                                    sx={{ fontSize: 18, ml: -0.4 }}
                                />
                            </Box>
                        </ControlButton>
                        </PermissionGuard>
                    </ControlBar>
                    <Box
                        display='flex'
                        flexDirection='row'
                        height='calc(100% - 47px)'
                        // width='100%'
                        sx={{
                            flex: 1,
                            // overflow: 'hidden',
                        }}
                    >
                        <Box
                            display='flex'
                            // flexDirection={'row'} 
                            // height='100%'
                            flex={0.28}
                            bgcolor={'rgb(0,0,0,0.8)'}
                            sx={{
                                overflow: 'scroll',
                            }}                    >
                            <List sx={{
                                color: 'white',
                                width: '100%',
                                bgcolor:'black'
                            }}
                            >
                            {sources.map((source, index) => {
                                return (
                                    <SourcesRightClick
                                        deleteSource={() => deleteSource(source)}
                                        source={source}
                                    >
                                        <ItemList
                                            active={source == currentSource}
                                            key={index}
                                            title={source} 
                                            onItemClick={() => {
                                                setCurrentSource(source)
                                            }}
                                        />
                                    </SourcesRightClick>
                                )
                            })}
                            </List>
                        </Box>
                        <Box
                            display='flex'
                            flexDirection={'row'} 
                            flex={0.85}
                            bgcolor='rgb(0,0,0,0.8)'
                            sx={{
                                overflowY: 'scroll'
                            }}
                        >
                            <SourceConfig
                                context={renderContext}
                                source={currentSource}
                                sourceProcessingConfig={sourceProcessingConfig}
                                setSourceProcessingConfig={setSourceProcessingConfig}
                                updateValue={updateValue}
                                savedUpdate={savedUpdate}
                            />
                        </Box>
                    </Box>
                </Box>
            }
            
            <MenuPopover 
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                handleClose={closeMenu}
                content={menuContent}
            />       
        </React.Fragment>
    )
}